/**
 * The leads data table and the two dialogs that feed it (add one by hand,
 * import a CSV with column mapping).
 *
 * Shared on purpose: the Leads tab and the campaign wizard's leads step render
 * THIS table, so thin rows, avatar colours, the campaign banner and the
 * template-derived columns can never diverge between the two.
 */
import React from 'react'
import { Button, Dialog, Field, Input, Select, Switch } from '@/components/ds'
import { Ic } from '@/components/icons'
import { Avatar, PageSizeSelect, Val } from '@/components/common'
import { customMergeFields } from '@shared/render'
import { requiredLeadFields, type RequiredLeadField } from '@shared/gaps'
import { deleteLead, getCampaignTemplate, listUnsubscribes } from '@/lib/db'
import { createLead, importLeads } from '@/lib/api'
import { useAsync } from '@/lib/useAsync'
import { useToast } from '@/lib/toast'
import { isValidEmail, relativeTime } from '@/lib/format'
import { parseCsvRaw } from '@/lib/csv'
import type { Lead } from '@shared/types'

/** Real-data-table page size, not a toy one. */
export const LEADS_PAGE_SIZE = 100

/** Custom-field columns shown inline; the rest stay on hover. */
const MAX_FIELD_COLUMNS = 3

const ellipsis: React.CSSProperties = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

interface ColDef {
  label: string
  width: string
  end?: boolean
  render: (r: Lead) => React.ReactNode
}

export function LeadsTable({
  rows,
  total,
  campaign,
  campaignNames,
  page,
  pageSize = LEADS_PAGE_SIZE,
  onPageChange,
  onPageSizeChange,
  onChanged,
  bodyMaxHeight,
}: {
  rows: Lead[]
  total: number
  /** Set = campaign banner + template-derived columns; null = Campaign column. */
  campaign?: { id: string; name: string } | null
  campaignNames?: Map<string, string>
  page: number
  pageSize?: number
  onPageChange: (page: number) => void
  /** When set, the footer offers the rows-per-page dropdown. The host owns the
   *  state (and its persistence) and must reset to page 0 on change. */
  onPageSizeChange?: (size: number) => void
  /** Called after a delete so the host can refetch. */
  onChanged: () => void
  /** Caps the scrolling body instead of filling the remaining height. */
  bodyMaxHeight?: number
}) {
  const toast = useToast()
  const [confirmDelete, setConfirmDelete] = React.useState<Lead | null>(null)
  const [busy, setBusy] = React.useState(false)

  const campaignId = campaign?.id ?? ''

  // The campaign's BOUND template tells us which merge fields it actually
  // uses — that's what drives the columns and the banner.
  const campaignTemplate = useAsync(
    () => (campaignId ? getCampaignTemplate(campaignId) : Promise.resolve(null)),
    [campaignId],
  )
  const suppression = useAsync(() => listUnsubscribes(1000), [])

  async function remove(lead: Lead) {
    setBusy(true)
    try {
      await deleteLead(lead.id)
      toast('info', 'Lead deleted', 'Its queue rows went with it')
      setConfirmDelete(null)
      onChanged()
    } catch (e) {
      toast('error', 'Delete failed', e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  /**
   * Which custom fields become columns. NOT hardcoded: the campaign's template
   * merge fields come first (that's what the emails actually reference), then
   * any extra keys the leads themselves carry.
   */
  const templateFields = customMergeFields(campaignTemplate.data ?? null)
  const fieldKeys: string[] = []
  if (campaignId) {
    for (const k of templateFields) if (!fieldKeys.includes(k)) fieldKeys.push(k)
    for (const r of rows) {
      for (const k of Object.keys(r.fields ?? {})) {
        if (!fieldKeys.includes(k)) fieldKeys.push(k)
      }
    }
  }

  // Cap the inline columns so rows stay one line; the overflow stays reachable
  // on hover and is announced in the header, never silently dropped.
  const shownFields = fieldKeys.slice(0, MAX_FIELD_COLUMNS)
  const hiddenFields = fieldKeys.slice(MAX_FIELD_COLUMNS)

  // Counts for the banner. Suppressed = on the unsubscribe list.
  const suppressedSet = new Set((suppression.data ?? []).map((u) => u.email.trim().toLowerCase()))
  const suppressedHere = rows.filter((r) => suppressedSet.has(r.email.toLowerCase())).length

  /** All field values, for the hover tooltip on capped columns. */
  const allFieldsTitle = (r: Lead) => {
    const entries = Object.entries(r.fields ?? {})
    if (!entries.length) return undefined
    return entries.map(([k, v]) => `${k}: ${v}`).join('\n')
  }

  const cols: ColDef[] = [
    {
      label: 'Lead',
      width: 'minmax(150px,1fr)',
      render: (r) => (
        <span
          title={allFieldsTitle(r)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}
        >
          <Avatar name={r.name || r.email} />
          <span
            style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', ...ellipsis }}
          >
            {r.name || '—'}
          </span>
        </span>
      ),
    },
    {
      label: 'Email',
      width: 'minmax(190px,1.3fr)',
      render: (r) => (
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 12,
            color: 'var(--text-muted)',
            ...ellipsis,
          }}
        >
          {r.email}
        </span>
      ),
    },
    ...(campaignId
      ? shownFields.map<ColDef>((k, idx) => ({
          label: k.replace(/_/g, ' '),
          width: 'minmax(90px,0.9fr)',
          render: (r) => {
            const v = (r.fields ?? {})[k]
            return v == null || String(v).trim() === '' ? (
              <span style={{ color: 'var(--text-faint)' }}>—</span>
            ) : (
              <span
                style={{
                  fontSize: 13,
                  // first custom field carries the accent, like the mock's Role
                  color: idx === 0 ? 'var(--accent-text)' : 'var(--text-secondary)',
                  fontWeight: idx === 0 ? 500 : 400,
                  ...ellipsis,
                }}
              >
                {String(v)}
              </span>
            )
          },
        }))
      : ([
          {
            label: 'Campaign',
            width: 'minmax(120px,0.9fr)',
            render: (r) => (
              <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', ...ellipsis }}>
                {campaignNames?.get(r.campaign_id) ?? '—'}
              </span>
            ),
          },
        ] as ColDef[])),
    {
      label: 'Added',
      width: '78px',
      end: true,
      render: (r) => (
        <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
          {relativeTime(r.created_at)}
        </span>
      ),
    },
    {
      label: '',
      width: '24px',
      end: true,
      render: (r) => <DeleteX onClick={() => setConfirmDelete(r)} />,
    },
  ]

  const TMPL = cols.map((c) => c.width).join(' ')
  const minGridWidth = campaignId ? 440 + fieldKeys.length * 100 + 130 : 800

  return (
    <div
      /* Opaque, not frosted: 100 rows of 12px text stay crisp only over a solid
         fill. Same frame as every other panel. */
      className="lm-solid"
      style={{
        flex: bodyMaxHeight ? 'none' : 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid var(--glass-edge)',
        borderRadius: 'var(--glass-radius)',
        overflow: 'hidden',
        background: 'var(--surface-box)',
        boxShadow: 'var(--glass-shadow)',
      }}
    >
      {/* per-campaign banner — replaced by a Campaign column on "All" */}
      {campaign && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '7px 12px',
            background: 'linear-gradient(90deg,#6f6ce6,#5b5bd6)',
            color: '#fff',
            flex: 'none',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600 }}>{campaign.name}</span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              padding: '1px 8px',
              borderRadius: 999,
              background: 'rgba(255,255,255,.22)',
            }}
          >
            {total.toLocaleString()} lead{total === 1 ? '' : 's'}
          </span>
          <div style={{ flex: 1 }} />
          <span
            style={{ fontSize: 11.5, color: 'rgba(255,255,255,.82)' }}
            title={
              hiddenFields.length
                ? `${hiddenFields.length} shown on row hover: ${hiddenFields.join(', ')}`
                : undefined
            }
          >
            fields: {fieldKeys.length ? fieldKeys.join(', ') : '—'}
            {hiddenFields.length > 0 && ` (${shownFields.length} as columns)`}
          </span>
          <span style={{ fontSize: 11.5, color: 'rgba(255,255,255,.82)' }}>
            {rows.length - suppressedHere} valid · {suppressedHere} suppressed
          </span>
        </div>
      )}

      <div className="scroll-x" style={{ flex: 'none' }}>
        <div style={{ minWidth: minGridWidth }}>
          {/* header strip */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: TMPL,
              gap: 10,
              alignItems: 'center',
              height: 30,
              padding: '0 12px',
              background: 'var(--surface-topbar)',
              borderBottom: '1px solid var(--border-default)',
            }}
          >
            {cols.map((c, i) => (
              <span
                key={i}
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: '#6a6f86',
                  textTransform: 'uppercase',
                  letterSpacing: '.05em',
                  justifySelf: c.end ? 'end' : undefined,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  minWidth: 0,
                }}
              >
                <span style={ellipsis}>{c.label}</span>
                {/* capped columns are announced on the last field column,
                    never silently dropped */}
                {i === 1 + shownFields.length && hiddenFields.length > 0 && (
                  <span
                    title={`Also on each lead — hover the name to see: ${hiddenFields.join(', ')}`}
                    style={{
                      fontSize: 9.5,
                      padding: '0 5px',
                      borderRadius: 999,
                      background: 'var(--accent-soft)',
                      color: 'var(--accent-text)',
                      whiteSpace: 'nowrap',
                      cursor: 'help',
                    }}
                  >
                    +{hiddenFields.length}
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* only the body scrolls */}
      <div
        className="scroll-x"
        style={{
          flex: bodyMaxHeight ? 'none' : 1,
          minHeight: 0,
          maxHeight: bodyMaxHeight,
          overflowY: 'auto',
        }}
      >
        <div style={{ minWidth: minGridWidth }}>
          {rows.map((r, i) => (
            <LeadRow key={r.id} cols={cols} row={r} tmpl={TMPL} alt={i % 2 === 1} />
          ))}
        </div>
      </div>

      {/* pagination footer, inside the box */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 10,
          padding: '6px 12px',
          background: 'var(--surface-box-header)',
          borderTop: '1px solid var(--border-default)',
          fontSize: 12,
          color: 'var(--text-muted)',
          flex: 'none',
        }}
      >
        <span style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span>
            {total === 0
              ? 'No leads'
              : `Showing ${page * pageSize + 1}–${Math.min(
                  (page + 1) * pageSize,
                  total,
                )} of ${total.toLocaleString()}`}
          </span>
          {onPageSizeChange && <PageSizeSelect value={pageSize} onChange={onPageSizeChange} />}
        </span>
        <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <Button
            variant="ghost"
            size="sm"
            disabled={page === 0}
            onClick={() => onPageChange(Math.max(0, page - 1))}
          >
            Previous
          </Button>
          <span
            className="tnum"
            style={{
              fontSize: 11.5,
              fontWeight: 600,
              padding: '2px 9px',
              borderRadius: 999,
              background: 'var(--accent)',
              color: 'var(--text-on-accent)',
            }}
          >
            {page + 1}
            {total > 0 ? ` / ${Math.max(1, Math.ceil(total / pageSize))}` : ''}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={(page + 1) * pageSize >= total}
            onClick={() => onPageChange(page + 1)}
          >
            Next
          </Button>
        </span>
      </div>

      <Dialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Delete this lead?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={busy}
              onClick={() => confirmDelete && remove(confirmDelete)}
            >
              Delete
            </Button>
          </>
        }
      >
        <Val muted={false}>{confirmDelete?.email}</Val> and any queue rows for it are removed.
        Delivery history in the send log is kept.
      </Dialog>
    </div>
  )
}

// -----------------------------------------------------------------------------

/** A column the campaign expects — green once the mapping supplies it. */
function ExpectChip({ label, required, met }: { label: string; required?: boolean; met: boolean }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 11,
        fontWeight: 600,
        padding: '1px 7px',
        borderRadius: 999,
        fontFamily: 'var(--font-mono)',
        background: met ? 'var(--status-approved-bg)' : 'var(--surface-card)',
        color: met ? 'var(--status-approved-fg)' : 'var(--text-muted)',
        border: `1px solid ${met ? 'transparent' : 'var(--border-default)'}`,
      }}
    >
      {met && <Ic.check size={10} />}
      {label}
      {required && !met && <span style={{ color: 'var(--danger-text)' }}>*</span>}
    </span>
  )
}

/** Faint ✕ that darkens on hover — quieter than a full IconButton in a thin row. */
function DeleteX({ onClick }: { onClick: () => void }) {
  const [hover, setHover] = React.useState(false)
  return (
    <button
      type="button"
      aria-label="Delete lead"
      title="Delete lead"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 20,
        height: 20,
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        borderRadius: 4,
        color: hover ? 'var(--danger-text)' : 'var(--text-faint)',
        transition: 'color var(--dur-fast) var(--ease-out)',
      }}
    >
      <Ic.x size={13} />
    </button>
  )
}

/** One thin data row: fixed height, single line, zebra + hover tint. */
function LeadRow({
  cols,
  row,
  tmpl,
  alt,
}: {
  cols: { end?: boolean; render: (r: Lead) => React.ReactNode }[]
  row: Lead
  tmpl: string
  alt: boolean
}) {
  const [hover, setHover] = React.useState(false)
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'grid',
        gridTemplateColumns: tmpl,
        gap: 10,
        alignItems: 'center',
        height: 33,
        padding: '0 12px',
        fontSize: 13,
        borderBottom: '1px solid var(--border-row)',
        background: hover
          ? 'var(--surface-topbar)'
          : alt
            ? 'var(--surface-row-alt)'
            : 'transparent',
      }}
    >
      {cols.map((c, i) => (
        <span key={i} style={c.end ? { justifySelf: 'end' } : { minWidth: 0 }}>
          {c.render(row)}
        </span>
      ))}
    </div>
  )
}

/** "job_title" -> "Job title", for a Field label. */
function humanizeToken(token: string): string {
  const s = token.replace(/_/g, ' ')
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export function AddLeadDialog({
  campaigns,
  defaultCampaignId,
  onClose,
  onAdded,
}: {
  campaigns: { value: string; label: string }[]
  defaultCampaignId: string
  onClose: () => void
  onAdded: (message: string) => void
}) {
  const toast = useToast()
  const [campaignId, setCampaignId] = React.useState(defaultCampaignId || campaigns[0]?.value || '')
  const [email, setEmail] = React.useState('')
  const [name, setName] = React.useState('')
  const [fieldValues, setFieldValues] = React.useState<Record<string, string>>({})
  const [busy, setBusy] = React.useState(false)

  // The selected campaign's BOUND template decides which merge inputs exist,
  // via the SAME requiredLeadFields the importer announces and the gate
  // enforces: one input per token that only resolves from leads.fields, and a
  // required Name whenever a token falls back to the name column. No template
  // bound -> email/name only. (There is deliberately no standalone "Company"
  // input any more — {{company}} is a sender constant answered by the
  // campaign, and a lead column of that name is ignored by the renderer.)
  const boundTemplate = useAsync(
    () => (campaignId ? getCampaignTemplate(campaignId) : Promise.resolve(null)),
    [campaignId],
  )
  const required = requiredLeadFields(boundTemplate.data ?? null)
  const needsName = required.some((f) => f.sources.includes('name'))
  const expected = required.filter((f) => !f.sources.includes('name')).map((f) => f.token)

  async function save() {
    if (!campaignId) {
      toast('error', 'Pick a campaign')
      return
    }
    if (!isValidEmail(email)) {
      toast('error', 'That email looks wrong')
      return
    }
    if (needsName && !name.trim()) {
      toast('error', 'This lead needs a name', 'The template merges {{name}} or {{first_name}}.')
      return
    }
    const missing = expected.filter((t) => !(fieldValues[t] ?? '').trim())
    if (missing.length) {
      toast(
        'error',
        `Fill in ${missing.map((t) => humanizeToken(t).toLowerCase()).join(', ')}`,
        `The template merges ${missing.map((t) => `{{${t}}}`).join(', ')} — the send would fail without it.`,
      )
      return
    }
    setBusy(true)
    try {
      const fields: Record<string, string> = {}
      for (const t of expected) fields[t] = (fieldValues[t] ?? '').trim()
      const result = await createLead({
        campaign_id: campaignId,
        email: email.trim(),
        name: name.trim() || undefined,
        fields: expected.length ? fields : undefined,
        source: 'manual',
      })
      onAdded(result.message)
    } catch (e) {
      toast('error', 'Could not add lead', e instanceof Error ? e.message : String(e))
      setBusy(false)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Add lead"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={busy} onClick={save}>
            {busy ? 'Adding…' : 'Add lead'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field
          label="Campaign"
          hint="The lead is queued as soon as you add it, with the campaign's delay applied."
        >
          <Select options={campaigns} value={campaignId} onChange={setCampaignId} />
        </Field>
        <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Email">
            <Input
              type="email"
              value={email}
              onChange={setEmail}
              placeholder="maria@northwind.io"
              autoFocus
            />
          </Field>
          <Field label={needsName ? 'Name' : 'Name (optional)'}>
            <Input value={name} onChange={setName} placeholder="Maria Chen" />
          </Field>
        </div>
        {expected.length > 0 && (
          <div
            className="grid-2"
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}
          >
            {expected.map((token) => (
              <Field key={token} label={humanizeToken(token)} hint={`Fills {{${token}}}`}>
                <Input
                  value={fieldValues[token] ?? ''}
                  onChange={(v) => setFieldValues((f) => ({ ...f, [token]: v }))}
                  placeholder={humanizeToken(token)}
                />
              </Field>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  )
}

// -----------------------------------------------------------------------------

type ColumnRole = 'email' | 'name' | 'first_name' | 'field' | 'ignore'

interface ColumnMapping {
  role: ColumnRole
  /** fields key, used only when role === 'field' */
  key: string
}

// "First name" writes fields.first_name — the place mergeVars reads FIRST for
// {{first_name}}. It is deliberately NOT folded into Name: putting a first
// name in the name column would corrupt what {{name}} renders.
const ROLE_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'name', label: 'Name' },
  { value: 'first_name', label: 'First name' },
  { value: 'field', label: 'Custom field' },
  { value: 'ignore', label: 'Ignore' },
]

/** Roles that may be assigned to at most one column. */
const SINGLE_USE_ROLES: ColumnRole[] = ['email', 'name', 'first_name']

/**
 * Emits a one-line CSV whose headers are exactly the columns this campaign's
 * template requires — email plus one column per required per-lead token, in
 * header spellings the importer auto-maps straight back to the right place.
 * `tokens: null` means no template is bound yet: offer the generic email,name.
 */
export function DownloadCsvTemplateButton({ tokens }: { tokens: string[] | null }) {
  return (
    <Button
      size="sm"
      variant="ghost"
      icon={<Ic.download size={14} />}
      onClick={() => {
        const headers = tokens === null ? ['email', 'name'] : ['email', ...tokens]
        const blob = new Blob([headers.join(',') + '\n'], { type: 'text/csv' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'leads-template.csv'
        a.click()
        URL.revokeObjectURL(url)
      }}
    >
      CSV template
    </Button>
  )
}

/** "Full Name" → full_name; blank → column_N. Keys must satisfy {{\w+}}. */
function slugKey(header: string, index: number): string {
  const k = header
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
  return k || `column_${index + 1}`
}

/**
 * Initial mapping guess. With headers: email/e-mail → Email, name/full name →
 * Name, everything else a custom field keyed by the header. Without headers:
 * the first column whose first value contains '@' → Email, the rest custom.
 */
function defaultMappings(raw: string[][], hasHeaders: boolean): ColumnMapping[] {
  const colCount = Math.max(0, ...raw.slice(0, 10).map((r) => r.length))
  const headers = hasHeaders ? (raw[0] ?? []) : []
  const firstData = (hasHeaders ? raw[1] : raw[0]) ?? []

  const maps: ColumnMapping[] = []
  let emailTaken = false
  let nameTaken = false
  let firstNameTaken = false

  for (let i = 0; i < colCount; i++) {
    if (hasHeaders) {
      const norm = (headers[i] ?? '').toLowerCase().replace(/[^a-z]/g, '')
      if (!emailTaken && (norm === 'email' || norm === 'emailaddress')) {
        maps.push({ role: 'email', key: 'email' })
        emailTaken = true
        continue
      }
      if (!nameTaken && (norm === 'name' || norm === 'fullname')) {
        maps.push({ role: 'name', key: 'name' })
        nameTaken = true
        continue
      }
      if (!firstNameTaken && (norm === 'firstname' || norm === 'givenname' || norm === 'forename')) {
        maps.push({ role: 'first_name', key: 'first_name' })
        firstNameTaken = true
        continue
      }
      maps.push({ role: 'field', key: slugKey(headers[i] ?? '', i) })
    } else {
      if (!emailTaken && String(firstData[i] ?? '').includes('@')) {
        maps.push({ role: 'email', key: 'email' })
        emailTaken = true
        continue
      }
      maps.push({ role: 'field', key: `column_${i + 1}` })
    }
  }
  return maps
}

export function ImportLeadsDialog({
  campaigns,
  defaultCampaignId,
  onClose,
  onDone,
}: {
  campaigns: { value: string; label: string }[]
  defaultCampaignId: string
  onClose: () => void
  onDone: (inserted: number, queued: number, skipped: number) => void
}) {
  const toast = useToast()
  const [campaignId, setCampaignId] = React.useState(defaultCampaignId || campaigns[0]?.value || '')
  const [raw, setRaw] = React.useState<string[][] | null>(null)
  const [hasHeaders, setHasHeaders] = React.useState(true)
  const [maps, setMaps] = React.useState<ColumnMapping[]>([])
  const [fileName, setFileName] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const inputRef = React.useRef<HTMLInputElement>(null)

  // What the chosen campaign's template actually merges, so we can tell the
  // user which columns to supply before they map anything.
  const chosenTemplate = useAsync(
    () => (campaignId ? getCampaignTemplate(campaignId) : Promise.resolve(null)),
    [campaignId],
  )
  // The SAME source the gate counts against — never customMergeFields, which
  // exempts builtins and used to claim "email only" for a {{first_name}}
  // template the gate would then block on.
  const required = requiredLeadFields(chosenTemplate.data ?? null)

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setFileName(file.name)
    try {
      const grid = parseCsvRaw(await file.text())
      if (!grid.length) {
        toast('error', 'That file has no rows')
        setRaw(null)
        setMaps([])
        return
      }
      setRaw(grid)
      setMaps(defaultMappings(grid, hasHeaders))
    } catch (err) {
      toast('error', "Couldn't read that file", err instanceof Error ? err.message : String(err))
    }
  }

  function toggleHeaders(v: boolean) {
    setHasHeaders(v)
    if (raw) setMaps(defaultMappings(raw, v))
  }

  /** Email/Name/First name are single-use: assigning one demotes the previous holder. */
  function setRole(index: number, role: ColumnRole) {
    setMaps((ms) =>
      ms.map((m, i) => {
        if (i === index) return { role, key: m.key }
        if (SINGLE_USE_ROLES.includes(role) && m.role === role) {
          return { role: 'field', key: m.key }
        }
        return m
      }),
    )
  }

  function setKey(index: number, key: string) {
    const clean = key.toLowerCase().replace(/[^a-z0-9_]+/g, '_')
    setMaps((ms) => ms.map((m, i) => (i === index ? { ...m, key: clean } : m)))
  }

  const headers = hasHeaders ? (raw?.[0] ?? []) : []
  const dataRows = raw ? (hasHeaders ? raw.slice(1) : raw) : []
  const emailIdx = maps.findIndex((m) => m.role === 'email')

  // Which resolution places the current mapping actually supplies.
  const mappedKeys = new Set(
    maps.flatMap((m) =>
      m.role === 'email'
        ? ['email']
        : m.role === 'name'
          ? ['name']
          : m.role === 'first_name'
            ? ['first_name']
            : m.role === 'field'
              ? [m.key]
              : [],
    ),
  )
  // A token is supplied when ANY of its sources gets a column — so
  // {{first_name}} is satisfied by a Name mapping OR a first_name column,
  // exactly as mergeVars resolves it.
  const supplies = (f: RequiredLeadField) =>
    f.sources.some((s) => mappedKeys.has(s === 'name' ? 'name' : s.slice('fields.'.length)))
  const missingFields = required.filter((f) => !supplies(f)).map((f) => f.token)
  const nameIdx = maps.findIndex((m) => m.role === 'name')
  const visible = maps.map((m, i) => ({ ...m, index: i })).filter((m) => m.role !== 'ignore')

  // Rows as they would import: email required, mapped fields keyed as chosen.
  const prepared =
    emailIdx < 0
      ? []
      : dataRows.flatMap((r) => {
          const email = (r[emailIdx] ?? '').trim()
          if (!isValidEmail(email)) return []
          const fields: Record<string, string> = {}
          maps.forEach((m, i) => {
            if (m.role !== 'field' && m.role !== 'first_name') return
            const v = (r[i] ?? '').trim()
            if (v) fields[m.role === 'first_name' ? 'first_name' : m.key] = v
          })
          const name = nameIdx >= 0 ? (r[nameIdx] ?? '').trim() : ''
          return [{ email, name: name || undefined, fields }]
        })

  const invalidCount = emailIdx >= 0 ? dataRows.length - prepared.length : 0

  async function upload() {
    if (!campaignId || !raw) return
    if (emailIdx < 0) {
      toast('error', 'Map one column to Email first')
      return
    }
    // BLOCKING: a file that can't supply a template token would import leads
    // doomed to "Unresolved merge fields". The wizard's reconciliation gate is
    // the backstop; this stops the bad import at the door.
    if (missingFields.length) {
      toast(
        'error',
        `Missing required column${missingFields.length === 1 ? '' : 's'}: ${missingFields.join(', ')}`,
        'Map a column to each merge field the template uses, or fix the template.',
      )
      return
    }
    const fieldMaps = maps.filter((m) => m.role === 'field')
    if (fieldMaps.some((m) => !m.key.trim())) {
      toast('error', 'Every custom field needs a key')
      return
    }
    // "First name" writes fields.first_name too, so it joins the collision check.
    const fieldKeys = maps.flatMap((m) =>
      m.role === 'field' ? [m.key] : m.role === 'first_name' ? ['first_name'] : [],
    )
    const dup = fieldKeys.find((k, i, a) => a.indexOf(k) !== i)
    if (dup) {
      toast('error', `Two columns share the field key "${dup}"`, 'Give one of them a different key')
      return
    }
    if (!prepared.length) {
      toast('error', 'No rows with a valid email')
      return
    }

    setBusy(true)
    try {
      const result = await importLeads({ campaign_id: campaignId, rows: prepared, source: 'csv' })
      onDone(result.inserted, result.queued, result.skipped.length + invalidCount)
    } catch (e) {
      toast('error', 'Import failed', e instanceof Error ? e.message : String(e))
      setBusy(false)
    }
  }

  const columnLabel = (m: { role: ColumnRole; key: string }) =>
    m.role === 'email'
      ? 'Email'
      : m.role === 'name'
        ? 'Name'
        : m.role === 'first_name'
          ? 'First name'
          : m.key.replace(/_/g, ' ')

  return (
    <Dialog
      open
      onClose={onClose}
      width={640}
      title="Import leads from CSV"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={busy || !prepared.length || !campaignId || missingFields.length > 0}
            onClick={upload}
          >
            {busy ? 'Importing…' : `Import ${prepared.length} lead${prepared.length === 1 ? '' : 's'}`}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Campaign">
          <Select options={campaigns} value={campaignId} onChange={setCampaignId} />
        </Field>

        {/* campaign-aware guidance: what this campaign's template merges */}
        {campaignId && (
          <div
            style={{
              padding: '8px 10px',
              borderRadius: 6,
              background: 'var(--accent-soft)',
              border: '1px solid var(--accent-soft-border)',
              fontSize: 12,
              color: 'var(--text-secondary)',
            }}
          >
            <div style={{ color: 'var(--accent-text)', fontWeight: 600, marginBottom: 3 }}>
              This campaign expects
            </div>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
              <ExpectChip label="email" required met={mappedKeys.has('email')} />
              {required.map((f) => (
                <ExpectChip key={f.token} label={f.token} met={supplies(f)} />
              ))}
              {required.length === 0 && (
                <span style={{ color: 'var(--text-muted)' }}>
                  no merge fields — this campaign's template only needs an email
                </span>
              )}
            </div>
            {raw && missingFields.length > 0 && (
              <div style={{ marginTop: 6, color: 'var(--danger-text)', fontWeight: 500 }}>
                Missing: {missingFields.join(', ')} — every imported lead would fail at send with
                an empty {missingFields.map((f) => `{{${f}}}`).join(', ')}. Map a column to it
                (or fix the template) to import.
              </div>
            )}
          </div>
        )}

        <Field label="File">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={onFile}
              style={{ display: 'none' }}
            />
            <Button
              variant="secondary"
              size="sm"
              icon={<Ic.upload size={14} />}
              onClick={() => inputRef.current?.click()}
            >
              Choose file
            </Button>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {fileName || 'No file selected'}
            </span>
            <DownloadCsvTemplateButton
              tokens={chosenTemplate.data ? required.map((f) => f.token) : null}
            />
            <div style={{ flex: 1 }} />
            {raw && (
              <Switch checked={hasHeaders} onChange={toggleHeaders} label="First row is headers" />
            )}
          </div>
        </Field>

        {raw && (
          <>
            {/* ---- column mapping ---- */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)' }}>
                Columns
              </div>
              {maps.map((m, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span
                    style={{
                      width: 130,
                      flex: 'none',
                      fontSize: 12,
                      color: 'var(--text-muted)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={hasHeaders ? headers[i] : undefined}
                  >
                    {hasHeaders ? headers[i] || `Column ${i + 1}` : `Column ${i + 1}`}
                  </span>
                  <div style={{ width: 140, flex: 'none' }}>
                    <Select
                      size="sm"
                      options={ROLE_OPTIONS}
                      value={m.role}
                      onChange={(v) => setRole(i, v as ColumnRole)}
                    />
                  </div>
                  {m.role === 'field' ? (
                    <Input
                      size="sm"
                      value={m.key}
                      onChange={(v) => setKey(i, v)}
                      placeholder="field_key"
                      style={{ flex: 1 }}
                    />
                  ) : (
                    <span
                      style={{
                        flex: 1,
                        fontSize: 12,
                        color: 'var(--text-faint)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {(dataRows[0]?.[i] ?? '').trim()}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* ---- preview of the mapped result ---- */}
            {visible.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)' }}>
                  Preview
                </div>
                <div className="scroll-x">
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr>
                        {visible.map((m) => (
                          <th
                            key={m.index}
                            style={{
                              textAlign: 'left',
                              padding: '3px 10px 3px 0',
                              fontSize: 11,
                              fontWeight: 500,
                              color: 'var(--text-muted)',
                              textTransform: 'uppercase',
                              letterSpacing: 'var(--tracking-wide)',
                              borderBottom: '1px solid var(--border-default)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {columnLabel(m)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {dataRows.slice(0, 3).map((r, ri) => (
                        <tr key={ri}>
                          {visible.map((m) => (
                            <td
                              key={m.index}
                              style={{
                                padding: '4px 10px 4px 0',
                                color: 'var(--text-secondary)',
                                borderBottom: '1px solid var(--border-default)',
                                whiteSpace: 'nowrap',
                                maxWidth: 180,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {(r[m.index] ?? '').trim() || '—'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {emailIdx < 0
                    ? 'Map one column to Email to continue.'
                    : `${prepared.length} of ${dataRows.length} rows have a valid email${
                        dataRows.length > 3 ? ` · showing first 3` : ''
                      }`}
                </div>
              </div>
            )}
          </>
        )}

        <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
          Duplicates (same email on the same campaign) and anyone on the suppression list are
          skipped. Every other lead is scheduled on arrival with the campaign's delay applied.
        </p>
      </div>
    </Dialog>
  )
}
